import type { LC, PropsWithChildren } from '@use-gpu/live';
import type { TextureSource } from '@use-gpu/core';
import type { Renderable } from '../pass';
import type { BoundLight } from '../light/types';
import { mat4 } from 'gl-matrix';

import { yeet, memo, useMemo } from '@use-gpu/live';
import { patch } from '@use-gpu/state';
import {
  makeDepthStencilAttachments, makeTexture,
  getCubeFaceLabel, getCubeFaceMatrix, reverseZ, updateViewProjection, updateViewSize,
} from '@use-gpu/core';

import { useDeviceContext } from '../providers/device-provider';
import { usePassContext } from '../providers/pass-provider';
import { QueueReconciler } from '../reconcilers/index';

import { useInspectable } from '../hooks/useInspectable';
import { useShader } from '../hooks/useShader';

import { useCopyDepth } from '../render/copy/value-copy';
import { SHADOW_FORMAT, SHADOW_PAGE } from '../render/light/light-data';

import { useDynamicViewBinding, useApplyPassBindGroup } from './bindings';
import { drawToPass } from './util';

import { getDepthQuadsToHemiSample } from '@use-gpu/wgsl/render/sample/depth-quads-to-hemi.wgsl';

const {quote} = QueueReconciler;

export type ShadowHemiPassProps = PropsWithChildren<{
  env: Record<string, any>,
  calls: {
    shadow?: Renderable[],
  },
  map: BoundLight,
  descriptors: GPURenderPassDescriptor[],
  texture: TextureSource,
}>;

const NO_OPS: any[] = [];
const toArray = <T>(x?: T[]): T[] => Array.isArray(x) ? x : NO_OPS;

const label = 'ShadowHemiPass';
const LABEL = { label };
const τ = Math.PI * 2;

const HEMI_FACES = [
  [2, 0, 0, 1],
  [0, 1, 1, 1],
  [1, 1, 2, 0],
  [4, 2, 1, 1],
  [5, 2, 2, 0],
];

/** Shadow render pass.

Draws all shadow calls to a hemispherical shadow map.
*/
export const ShadowHemiPass: LC<ShadowHemiPassProps> = memo((props: ShadowHemiPassProps) => {
  const {
    env,
    calls,
    map,
    descriptors: shadowMapDescriptors,
  } = props;

  const inspect = useInspectable();

  const device = useDeviceContext();
  const {
    targets: {shadow: [renderContext]},
    bindGroups: {view: viewBindGroup},
  } = usePassContext();

  const shadows = toArray(calls['shadow'] as Renderable[]);

  // Bind to dynamic view
  const {bindGroup, cull, uniforms, upload: uploadView} = useDynamicViewBinding(viewBindGroup);
  const {bindPass, dataBindings} = useApplyPassBindGroup(env, bindGroup);

  const {
    shadow,
    shadowMap,
    shadowUV,
    shadowDepth,
  } = map;

  const {
    depth: nearFar,
    depth: [near, far],
    size: [width, height],
    unit: [unitX, unitY],
    resolution,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  } = shadow!;

  const [hemiSource, hemiSampler, hemiSize, hemiDescriptors] = useMemo(() => {
    const s = Math.round(Math.max(width, height) * (resolution ?? 0.707) / 2) * 2;

    const texture = makeTexture(
      device,
      s,
      s,
      3,
      SHADOW_FORMAT,
      GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
      1,
      1,
      '2d',
    );
    texture.label = label;

    const attachments = makeDepthStencilAttachments(texture, SHADOW_FORMAT, 3);

    const descriptors = attachments.map((depthStencilAttachment) => ({
      label,
      colorAttachments: [],
      depthStencilAttachment,
    }));

    const viewDescriptors = HEMI_FACES.map(([face, layer,, clear]) => patch(descriptors[layer], {
      label: `<ShadowHemiPass> #${getCubeFaceLabel(face)}`,
      depthStencilAttachment: {
        depthLoadOp: clear ? 'clear' : 'load',
      },
    }));

    const sampler = {
      sampler: {
        minFilter: 'nearest',
        magFilter: 'nearest',
        mipmapFilter: 'nearest',
      },
      filter: 'non-filtering',
    };

    const source = {
      texture,
      length: s*s*3,
      size: [s, s, 3],
      format: SHADOW_FORMAT,
      layout: 'texture_depth_2d_array',
      hint: 'depth',
      version: 0,
    };

    return [source, sampler, s, viewDescriptors];
  }, [device, width, height, resolution]);

  const [projectionMatrix, halfProjectionMatrix, viewMatrix] = useMemo(() => {
    const m = mat4.perspectiveZO(mat4.create(), τ/4, 1, near, far);
    reverseZ(m, m);

    const h = mat4.create();
    mat4.multiply(h, [
      1, 0, 0, 0,
      0, 2, 0, 0,
      0, 0, 1, 0,
      0,-1, 0, 1,
    ], m);

    // For seamless cubemap filtering, ensure each face's texels go edge-to-edge
    const sm = (hemiSize - 1) / hemiSize;
    const sh = (hemiSize / 2 - 1) / (hemiSize / 2);
    mat4.multiply(m, [
      sm, 0, 0, 0,
      0, sm, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1,
    ], m);
    mat4.multiply(h, [
      sm, 0, 0, 0,
      0, sh, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1,
    ], h);

    return [m, h, mat4.create()];
  }, [hemiSize, near, far]);

  updateViewProjection(uniforms, undefined, undefined, undefined, nearFar);

  const getDepth = useShader(
    getDepthQuadsToHemiSample, [
      hemiSource,
      hemiSampler,
      hemiSource.size,
      shadowDepth,
    ]
  );

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const blit = useCopyDepth(renderContext, getDepth, null, shadowUV!, SHADOW_PAGE);

  const inspected = inspect({
    output: {
      depth: hemiSource,
    },
    pass: {
      uniforms,
      bindings: dataBindings,
    },
    render: {
      vertices: 0,
      triangles: 0,
    },
  });

  return quote(yeet(() => {
    let vs = 0;
    let ts = 0;

    const {into} = map;
    const countGeometry = (v: number, t: number) => { vs += v; ts += t; };

    for (let i = 0; i < 5; ++i) {
      const [face,, side] = HEMI_FACES[i];
      const pm = side ? halfProjectionMatrix : projectionMatrix;

      const [w, height] = hemiSource.size;
      const y = side > 1 ? height / 2 : 0;
      const h = side ? height / 2 : height;

      // Update view
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      mat4.multiply(viewMatrix, getCubeFaceMatrix(face), into!);
      updateViewProjection(uniforms, pm, viewMatrix);
      updateViewSize(uniforms, width, h, 1, unitX, unitY, 1);
      uploadView(uniforms);

      // Render pass
      const commandEncoder = device.createCommandEncoder(LABEL);
      const passEncoder = commandEncoder.beginRenderPass(hemiDescriptors[i]);

      bindPass?.(passEncoder);

      passEncoder.setViewport(0, y, w, h, 0, 1);
      passEncoder.setScissorRect(0, y, w, h);

      drawToPass(cull, shadows, passEncoder, countGeometry, uniforms, 1, true);

      passEncoder.end();

      const command = commandEncoder.finish();
      device.queue.submit([command]);
    }

    {
      const commandEncoder = device.createCommandEncoder(LABEL);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const passEncoder = commandEncoder.beginRenderPass(shadowMapDescriptors[shadowMap!]);
      blit(passEncoder);
      passEncoder.end();

      const command = commandEncoder.finish();
      device.queue.submit([command]);
    }

    inspected.render.vertices = vs;
    inspected.render.triangles = ts;

    return null;
  }));
}, 'ShadowHemiPass');
